import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const APP_URL = process.env.REACT_APP_URL || 'http://localhost:3000';

const WhatsNext = ({ gymData }) => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedText, setCopiedText] = useState(null);
  const [qrCodes, setQrCodes] = useState({});

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch active boards from the boards table (onboarding is complete at this point)
      const response = await axios.get(`${API_URL}/api/boards/gym/${gymData.gym.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const boardsData = response.data || [];
      setBoards(boardsData);

      // Generate QR codes for each board
      const qrPromises = boardsData.map(async (board) => {
        const qrUrl = `${APP_URL}/qr/board/${board.id}`;
        const qrDataUrl = await QRCode.toDataURL(qrUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#0F172A',
            light: '#FFFFFF'
          }
        });
        return { id: board.id, url: qrDataUrl };
      });

      const qrResults = await Promise.all(qrPromises);
      const qrMap = qrResults.reduce((acc, { id, url }) => {
        acc[id] = url;
        return acc;
      }, {});

      setQrCodes(qrMap);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch boards:', err);
      setLoading(false);
    }
  };

  const bookingUrl = `${APP_URL}/gyms/${gymData.gym.slug || gymData.gym.id}`;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const socialMediaPosts = {
    facebook: `📋 Hey members! We're now using BoardRez to manage our climbing board reservations!\n\nBook your spot online: ${bookingUrl}\n\nLock in your training time and get strong - reserve your favorite board in seconds! 🧗‍♀️`,

    instagram: `📋 Reserve your board ⏰\n\nWe're stoked to announce BoardRez - our new online board reservation system!\n\n✨ Book your favorite board\n⏱️ See real-time availability\n📱 Manage reservations from your phone\n\nLink in bio or visit: ${bookingUrl}\n\n#climbing #boardclimbing #${gymData.gym.name.replace(/\s+/g, '')}`,

    twitter: `🧗 New! Reserve your climbing board online with BoardRez\n\nBook instantly: ${bookingUrl}\n\nNo more waiting - climb on your schedule! 📋✨`
  };

  const emailCopy = `Subject: New! Online Board Reservations with BoardRez

Hi Climbers!

We're excited to announce that we've partnered with BoardRez to bring you online board reservations!

🎯 What's New:
• Reserve your favorite board online
• See real-time board availability
• Manage your reservations from any device
• Get automatic reminders before your session

🚀 Get Started:
Visit ${bookingUrl} to make your first reservation!

This new system will allow individuals to prioritize training times, while still leaving flexibility for social climbing.

As always, remember to be kind and consider your fellow human beings!

Questions? Just reply to this email or stop by the front desk.

Happy climbing!
${gymData.gym.name} Team`;

  const downloadQRCode = (boardId, boardName) => {
    const link = document.createElement('a');
    link.download = `${boardName.replace(/\s+/g, '_')}_QR_Code.png`;
    link.href = qrCodes[boardId];
    link.click();
  };

  const printSignage = () => {
    const printWindow = window.open('', '_blank');
    const signageHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${gymData.gym.name} - BoardRez Flyer</title>
        <style>
          @page { size: letter; margin: 0; }
          body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background: linear-gradient(135deg, #5EEAD4 0%, #A78BFA 50%, #F472B6 100%);
            height: 100vh;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .flyer {
            background: white;
            padding: 36px 32px;
            border: 4px solid #0F172A;
            box-shadow: 8px 8px 0 rgba(15, 23, 42, 0.2);
            max-width: 600px;
            text-align: center;
          }
          h1 {
            font-size: 36px;
            font-weight: bold;
            color: #0F172A;
            margin: 0 0 8px 0;
            line-height: 1.2;
          }
          .tagline {
            font-size: 18px;
            color: #475569;
            margin-bottom: 20px;
            font-weight: 600;
          }
          .qr-section {
            background: linear-gradient(135deg, #F0FDFA 0%, #FAF5FF 50%, #FDF4FF 100%);
            padding: 20px;
            border: 3px solid #0F172A;
            margin: 20px 0;
          }
          .qr-code {
            width: 180px;
            height: 180px;
            margin: 12px auto;
            border: 3px solid #0F172A;
            background: white;
            padding: 8px;
          }
          .url {
            font-size: 20px;
            font-weight: bold;
            color: #14B8A6;
            margin-top: 12px;
            word-break: break-all;
          }
          .features {
            text-align: left;
            margin: 20px 0;
          }
          .feature {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            font-size: 15px;
          }
          .feature-icon {
            width: 26px;
            height: 26px;
            background: #14B8A6;
            border: 2px solid #0F172A;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            flex-shrink: 0;
            font-size: 16px;
          }
          .footer {
            margin-top: 20px;
            font-size: 14px;
            color: #64748B;
          }
        </style>
      </head>
      <body>
        <div class="flyer">
          <h1>📋 Reserve Your<br>Board Online!</h1>
          <div class="tagline">Stop waiting. Start climbing.</div>

          <div class="features">
            <div class="feature">
              <div class="feature-icon">✓</div>
              <div><strong>Book 24/7</strong> - Reserve anytime, anywhere</div>
            </div>
            <div class="feature">
              <div class="feature-icon">✓</div>
              <div><strong>See Availability</strong> - Real-time board status</div>
            </div>
            <div class="feature">
              <div class="feature-icon">✓</div>
              <div><strong>Get Reminders</strong> - Never miss your session</div>
            </div>
            <div class="feature">
              <div class="feature-icon">✓</div>
              <div><strong>Manage Anywhere</strong> - From any device</div>
            </div>
          </div>

          <div class="qr-section">
            <div style="font-size: 16px; font-weight: bold; color: #0F172A; margin-bottom: 6px;">
              Scan to Book Now
            </div>
            <div class="qr-code">
              <img src="${qrCodes[boards[0]?.id] || ''}" alt="QR Code" style="width: 100%; height: 100%;" />
            </div>
            <div style="font-size: 14px; color: #64748B; margin-bottom: 4px;">or visit</div>
            <div class="url">${bookingUrl}</div>
          </div>

          <div class="footer">
            ${gymData.gym.name}<br>
            Powered by BoardRez
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(signageHTML);
    printWindow.document.close();

    // Wait for images to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border-2 border-slate-900 shadow-retro text-center">
        <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-lg font-semibold text-slate-700">Loading your marketing materials...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
          📣 Spread the Word!
        </h2>
        <p className="text-lg text-slate-700">
          Get your members excited about BoardRez with these ready-to-use marketing materials
        </p>
      </div>

      {/* Section 1: Printable Signage */}
      <div className="mb-8 pb-8 border-b-2 border-slate-900">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center border-2 border-slate-900">
            <span className="text-white font-bold text-lg">🖨️</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">Printable Signage & Flyers</h3>
        </div>
        <p className="text-slate-700 mb-4">
          Print professional flyers to post around your gym or near your boards
        </p>
        <button
          onClick={printSignage}
          className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
        >
          🖨️ Print Flyer (Letter Size)
        </button>
      </div>

      {/* Section 2: QR Codes */}
      <div className="mb-8 pb-8 border-b-2 border-slate-900">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center border-2 border-slate-900">
            <span className="text-white font-bold text-lg">📱</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">Board QR Codes</h3>
        </div>
        <p className="text-slate-700 mb-4">
          Download QR codes for each board to place near the physical boards
        </p>

        {boards.length === 0 ? (
          <div className="bg-sunshine-100 border-2 border-sunshine-500 rounded-lg p-4">
            <p className="text-sunshine-800 font-semibold text-sm">
              No boards found. Make sure you activated boards during the onboarding process.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {boards.map((board) => (
              <div key={board.id} className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900">
                <div className="text-center">
                  <p className="font-bold text-slate-900 mb-2">{board.name}</p>
                  {qrCodes[board.id] && (
                    <>
                      <img
                        src={qrCodes[board.id]}
                        alt={`QR Code for ${board.name}`}
                        className="w-32 h-32 mx-auto mb-3 border-2 border-slate-900"
                      />
                      <button
                        onClick={() => downloadQRCode(board.id, board.name)}
                        className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
                      >
                        Download QR Code
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 3: Social Media */}
      <div className="mb-8 pb-8 border-b-2 border-slate-900">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-magenta-500 rounded-full flex items-center justify-center border-2 border-slate-900">
            <span className="text-white font-bold text-lg">📱</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">Social Media Posts</h3>
        </div>
        <p className="text-slate-700 mb-4">
          Ready-to-post announcements for your social media channels
        </p>

        <div className="space-y-4">
          {/* Facebook */}
          <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900">
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-slate-900 flex items-center gap-2">
                <span className="text-blue-600">📘</span> Facebook
              </p>
              <button
                onClick={() => copyToClipboard(socialMediaPosts.facebook, 'facebook')}
                className="px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
              >
                {copiedText === 'facebook' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-300">
              {socialMediaPosts.facebook}
            </p>
          </div>

          {/* Instagram */}
          <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900">
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-slate-900 flex items-center gap-2">
                <span className="text-pink-600">📷</span> Instagram
              </p>
              <button
                onClick={() => copyToClipboard(socialMediaPosts.instagram, 'instagram')}
                className="px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
              >
                {copiedText === 'instagram' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-300">
              {socialMediaPosts.instagram}
            </p>
          </div>

          {/* Twitter/X */}
          <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900">
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-slate-900 flex items-center gap-2">
                <span className="text-slate-900">𝕏</span> Twitter / X
              </p>
              <button
                onClick={() => copyToClipboard(socialMediaPosts.twitter, 'twitter')}
                className="px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
              >
                {copiedText === 'twitter' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-300">
              {socialMediaPosts.twitter}
            </p>
          </div>
        </div>
      </div>

      {/* Section 4: Email Newsletter */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-coral-500 rounded-full flex items-center justify-center border-2 border-slate-900">
            <span className="text-white font-bold text-lg">📧</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">Email Newsletter Copy</h3>
        </div>
        <p className="text-slate-700 mb-4">
          Copy this text into your next member newsletter or email blast
        </p>

        <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-slate-900">Ready-to-Send Email</p>
            <button
              onClick={() => copyToClipboard(emailCopy, 'email')}
              className="px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              {copiedText === 'email' ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-300 max-h-96 overflow-y-auto">
            {emailCopy}
          </p>
        </div>
      </div>

      {/* Quick Tip */}
      <div className="bg-sunshine-100 border-2 border-sunshine-500 rounded-lg p-4 mb-6">
        <p className="text-sunshine-800 font-semibold text-sm">
          💡 <strong>Pro Tip:</strong> Post the QR codes near each physical board so climbers can scan and book on the spot!
        </p>
      </div>

      {/* CTA Button */}
      <div className="text-center">
        <button
          onClick={() => navigate('/dashboard')}
          className="px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all text-lg"
        >
          Go to Dashboard →
        </button>
      </div>
    </div>
  );
};

export default WhatsNext;
